/**
 * Onboarding Testing Utilities
 * Helper functions for testing the new reality-based onboarding flow
 */

import { enableRealityOnboarding } from './featureFlags';

/**
 * Test the new onboarding flow
 * This function helps validate the implementation
 */
export function testNewOnboarding(): void {
  console.log('🧪 Testing New Onboarding Flow');
  console.log('================================');
  
  // Enable the new onboarding
  enableRealityOnboarding();
  
  console.log('📋 Test Checklist:');
  console.log('✅ 1. Refresh the page');
  console.log('✅ 2. Clear onboarding completion (if needed): clearOnboardingState()');
  console.log('✅ 3. Navigate to a protected route to trigger onboarding');
  console.log('✅ 4. Test all 4 steps:');
  console.log('   - Step 1: Professional Account Creation + User Profiling');
  console.log('   - Step 2: Memory Space Architecture Introduction');
  console.log('   - Step 3: Portal Experience + First Memory Creation');
  console.log('   - Step 4: Integration Showcase & Next Steps');
  console.log('✅ 5. Verify analytics tracking in PostHog');
  console.log('');
  console.log('🎯 Expected Features:');
  console.log('• No email field (user already signed in)');
  console.log('• "Other" challenge option with free text');
  console.log('• Proper button alignment in space creation');
  console.log('• AI auto-tagging and multimedia roadmap notes');
  console.log('• Real company logos (Anthropic, OpenAI, n8n, etc.)');
  console.log('• Correct socket types (API, MCP, Events, Webhooks)');
  console.log('• 700ms portal transitions');
  console.log('');
  console.log('🔧 Debug Commands:');
  console.log('• disableRealityOnboarding() - Switch back to old flow');
  console.log('• clearOnboardingState() - Reset onboarding completion');
}

/**
 * Clear onboarding completion state
 * Forces onboarding to show again for testing
 */
export function clearOnboardingState(): void {
  // Clear common onboarding completion flags
  const keysToRemove = [
    'memara_onboarding_completed',
    'onboarding_completed',
    'user_onboarded',
    'has_completed_onboarding'
  ];
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  
  console.log('🧹 Onboarding state cleared! Refresh to see onboarding again.');
  console.log('💡 Note: You may need to sign out and back in if user profile has onboarding flags.');
}

/**
 * Get current onboarding status for debugging
 */
export function debugOnboardingStatus(): void {
  console.log('🔍 Onboarding Debug Status');
  console.log('==========================');
  
  // Check localStorage flags
  const localStorageFlags = [
    'memara_onboarding_completed',
    'onboarding_completed', 
    'user_onboarded',
    'has_completed_onboarding',
    'memara_enable_reality_onboarding'
  ];
  
  console.log('📱 LocalStorage Flags:');
  localStorageFlags.forEach(flag => {
    const value = localStorage.getItem(flag);
    console.log(`  ${flag}: ${value}`);
  });
  
  console.log('🌍 Environment Variables:');
  console.log(`  VITE_ENABLE_REALITY_ONBOARDING: ${import.meta.env.VITE_ENABLE_REALITY_ONBOARDING}`);
  
  console.log('🎯 Feature Flag Status:');
  console.log(`  Reality Onboarding Enabled: ${import.meta.env.VITE_ENABLE_REALITY_ONBOARDING === 'true' || localStorage.getItem('memara_enable_reality_onboarding') === 'true'}`);
}

/**
 * Simulate onboarding completion for testing
 */
export function simulateOnboardingCompletion(): void {
  localStorage.setItem('memara_onboarding_completed', 'true');
  console.log('✅ Onboarding marked as completed. Refresh to skip onboarding.');
  console.log('💡 Use clearOnboardingState() to reset.');
}

/**
 * Debug user metadata to see what name information is available
 */
export function debugUserMetadata(): void {
  const userStr = localStorage.getItem('sb-wjxzjnxmxkgqnwzqvlmp-auth-token');
  if (userStr) {
    try {
      const authData = JSON.parse(userStr);
      const user = authData?.user;
      
      console.log('🔍 User Metadata Debug');
      console.log('======================');
      console.log('Email:', user?.email);
      console.log('Full Name (user_metadata):', user?.user_metadata?.full_name);
      console.log('Name (user_metadata):', user?.user_metadata?.name);
      console.log('All user_metadata:', user?.user_metadata);
      console.log('');
      console.log('🎯 Dashboard Display Logic:');
      console.log('Will show:', 
        user?.user_metadata?.full_name?.split(' ')[0] || 
        user?.user_metadata?.name?.split(' ')[0] || 
        user?.email?.split('@')[0] || 
        'No name found'
      );
    } catch (error) {
      console.error('Failed to parse user data:', error);
    }
  } else {
    console.log('❌ No user authentication data found');
  }
}

// Make functions available globally for easy console access
if (typeof window !== 'undefined') {
  (window as any).testNewOnboarding = testNewOnboarding;
  (window as any).clearOnboardingState = clearOnboardingState;
  (window as any).debugOnboardingStatus = debugOnboardingStatus;
  (window as any).simulateOnboardingCompletion = simulateOnboardingCompletion;
  (window as any).debugUserMetadata = debugUserMetadata;
} 